import { useEffect } from "react";
import { jwtDecode } from "jwt-decode";

export default function useSessionGuard() {
  useEffect(() => {
    const token = sessionStorage.getItem("jswtoken");
    if (!token) {
      return;
    }
    let hasRedirected = false;
    const safeRedirect = (reason) => {
      if (hasRedirected) return;
      hasRedirected = true;
      let message = "Session expired. Please launch again from Moodle.";
      if (reason === "idle") {
        message = "Session expired due to inactivity.";
      }
      if (reason === "expired") {
        message = "Your session has expired.";
      }
      alert(message);
      setTimeout(() => {
        sessionStorage.clear();
        localStorage.clear();

        window.location.href =
          `/session-expired?reason=${reason}`;
      }, 1000);
    };
    try {
      const decoded = jwtDecode(token);
      const now = Math.floor(Date.now() / 1000);
      if ( !decoded.exp || decoded.exp < now) {
        throw new Error("expired");
      }
      let customExpiry = sessionStorage.getItem("custom_expiry");
      if (!customExpiry) {
        customExpiry = now + (30 * 60);
        sessionStorage.setItem("custom_expiry", customExpiry);
      }
      window.history.replaceState(
        {},
        document.title,
        window.location.pathname
      );

    } catch (e) {
      safeRedirect("invalid");
      return;
    }
    const IDLE_LIMIT = 30 * 60 * 1000;
    let idleTimer;
    let expiryTimer;
    const scheduleTimers = () => {
      clearTimeout(idleTimer);
      clearTimeout(expiryTimer);
      const extendedExpiry = Math.floor(Date.now() / 1000) + (30 * 60);
      sessionStorage.setItem("custom_expiry", extendedExpiry);
      idleTimer = setTimeout(() => {
        safeRedirect("idle");
      }, IDLE_LIMIT);

      expiryTimer = setTimeout(() => {
        safeRedirect("expired");
      }, IDLE_LIMIT);
    };
    const resetIdle = (source = "unknown") => {
      if (hasRedirected) {
        return;
      }
      scheduleTimers();
    };
    const currentExpiry = Number(sessionStorage.getItem("custom_expiry"));
    if (Math.floor(Date.now() / 1000) > currentExpiry) {
      safeRedirect("expired");
      return;
    }
    // Browser activity
    const events = [
      "mousemove",
      "keydown",
      "click",
      "scroll"
    ];
    const handlers = {};
    events.forEach((event) => {
      handlers[event] = () => resetIdle(event);
      window.addEventListener(event, handlers[event]);
    });

    // TinyMCE activity
    const attachTinyMCEListeners = () => {
        const editor = window?.tinymce?.activeEditor;
        if (!editor || !editor.initialized) {
          return;
        }
        if (editor._sessionGuardAttached) {
          return;
        }
        const tinyHandler =
          () => {
            resetIdle(`tinymce:${editor.id}`);
          };
        editor._sessionGuardHandler = tinyHandler;
        editor.on("keydown input change paste", tinyHandler);
        editor._sessionGuardAttached = true;
      };
    const detachTinyMCEListeners = () => {
        const editor = window?.tinymce?.activeEditor;
        if (!editor || !editor._sessionGuardHandler) {
          return;
        }
        editor.off("keydown input change paste", editor._sessionGuardHandler);
        delete editor._sessionGuardHandler;
        delete editor._sessionGuardAttached;
      };
    attachTinyMCEListeners();
    const tinyCheck = setInterval(() => {
        attachTinyMCEListeners(); 
    }, 1000);
    scheduleTimers();
    return () => {
      clearTimeout(idleTimer);
      clearTimeout(expiryTimer);
      clearInterval(tinyCheck);
      events.forEach(
        (event) => {
          window.removeEventListener(event, handlers[event]);
        }
      );
      detachTinyMCEListeners();
    };
  }, []);
}